// GtFormFields — renders a form's questions. One renderer for the public form,
// the builder's live preview and nothing else, so what Jimmy previews is
// exactly what people see. Also the helpers that turn an answer into text for
// the candidate record, the response list and the CSV.
import { useState } from 'react';

export const QTYPES = [
  { type: 'short', label: 'Short answer', ico: 'M4 7h16 M4 12h10' },
  { type: 'paragraph', label: 'Paragraph', ico: 'M4 6h16 M4 10h16 M4 14h16 M4 18h10' },
  { type: 'choice', label: 'Multiple choice', ico: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
  { type: 'checkboxes', label: 'Checkboxes', ico: 'M4 5h16v14H4z M8 12l3 3 5-6' },
  { type: 'dropdown', label: 'Dropdown', ico: 'M4 6h16v12H4z M9 11l3 3 3-3' },
  { type: 'scale', label: 'Linear scale', ico: 'M3 12h18 M6 9v6 M12 9v6 M18 9v6' },
  { type: 'yesno', label: 'Yes or no', ico: 'M5 12l4 4 10-10' },
  { type: 'date', label: 'Date', ico: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4' },
  { type: 'daterange', label: 'Date range', ico: 'M4 6h16v14H4z M4 10h16 M8 14h8' },
  { type: 'time', label: 'Time', ico: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2' },
  { type: 'number', label: 'Number', ico: 'M9 4 7 20 M17 4l-2 16 M4 9h17 M3 15h17' },
  { type: 'email', label: 'Email', ico: 'M4 6h16v12H4z M4 7l8 6 8-6' },
  { type: 'phone', label: 'Phone', ico: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z' },
  { type: 'url', label: 'Link', ico: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1 M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1' },
  // C1-SHARECHECK: yes or no, tied to a role. A yes is consent to be put forward for it.
  { type: 'put_forward', label: 'Put forward for a role', ico: 'M5 12h11 M12 6l6 6-6 6 M4 4v16' },
  { type: 'section', label: 'Section', ico: 'M4 5h16 M4 12h16 M4 19h16' },
];
export const QTYPE = QTYPES.reduce((m, t) => { m[t.type] = t; return m; }, {});
export const HAS_OPTIONS = { choice: true, checkboxes: true, dropdown: true };

export function QIcon({ d, cls }) {
  return (
    <svg className={cls || 'gtff-ico'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {String(d).split(' M').map((p, i) => <path key={i} d={(i ? 'M' : '') + p} />)}
    </svg>
  );
}

function ukDay(s) {
  if (!s) return '';
  const d = new Date(String(s).length === 10 ? s + 'T12:00:00Z' : s);
  return isNaN(d) ? String(s) : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// One answer as plain words.
export function answerText(q, v) {
  if (v == null || v === '') return '';
  switch (q && q.type) {
    case 'checkboxes': return (Array.isArray(v) ? v : [v]).join(', ');
    case 'yesno':
    case 'put_forward': return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : String(v);
    case 'date': return ukDay(v);
    case 'daterange': return v && v.from ? ukDay(v.from) + ' to ' + ukDay(v.to) : '';
    case 'scale': return String(v) + ' of ' + q.max;
    case 'number': return Number(v).toLocaleString('en-GB');
    default: return String(v);
  }
}

// What is still missing, checked in the browser so people see it at once.
// The server checks everything again.
export function missingRequired(questions, answers) {
  const out = {};
  (questions || []).forEach((q) => {
    if (q.type === 'section' || !q.required) return;
    const v = answers && answers[q.key];
    const empty = v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length)
      || (q.type === 'daterange' && (!v || !v.from || !v.to))
      || ((q.type === 'choice' || q.type === 'checkboxes') && (Array.isArray(v) ? v : [v]).every((x) => x === 'Other: '));
    if (empty) out[q.key] = 'Please answer this.';
  });
  return out;
}

function OtherInput({ value, onChange, disabled }) {
  return (
    <input className="gtff-other" value={value} disabled={disabled} placeholder="Type your answer"
      onChange={(e) => onChange(e.target.value)} aria-label="Other answer" />
  );
}

function Choice({ q, value, onChange, disabled }) {
  const isOther = typeof value === 'string' && value.indexOf('Other: ') === 0;
  const [otherText, setOtherText] = useState(isOther ? value.slice(7) : '');
  return (
    <div className="gtff-opts" role="radiogroup">
      {q.options.map((o) => (
        <button type="button" key={o} role="radio" aria-checked={value === o} disabled={disabled}
          className={'gtff-opt' + (value === o ? ' gtff-opt--on' : '')} onClick={() => onChange(value === o && !q.required ? null : o)}>
          <span className="gtff-dot" />{o}
        </button>
      ))}
      {q.other && (
        <div className={'gtff-opt gtff-opt--other' + (isOther ? ' gtff-opt--on' : '')}>
          <button type="button" role="radio" aria-checked={isOther} disabled={disabled} className="gtff-optbtn"
            onClick={() => onChange('Other: ' + otherText)}><span className="gtff-dot" />Other</button>
          <OtherInput value={otherText} disabled={disabled} onChange={(t) => { setOtherText(t); onChange('Other: ' + t); }} />
        </div>
      )}
    </div>
  );
}

function Checks({ q, value, onChange, disabled }) {
  const list = Array.isArray(value) ? value : [];
  const otherVal = list.find((x) => String(x).indexOf('Other: ') === 0);
  const [otherText, setOtherText] = useState(otherVal ? otherVal.slice(7) : '');
  const toggle = (o) => onChange(list.indexOf(o) !== -1 ? list.filter((x) => x !== o) : list.concat([o]));
  const setOther = (on, text) => {
    const rest = list.filter((x) => String(x).indexOf('Other: ') !== 0);
    onChange(on ? rest.concat(['Other: ' + text]) : rest);
  };
  return (
    <div className="gtff-opts">
      {q.options.map((o) => (
        <button type="button" key={o} role="checkbox" aria-checked={list.indexOf(o) !== -1} disabled={disabled}
          className={'gtff-opt gtff-opt--box' + (list.indexOf(o) !== -1 ? ' gtff-opt--on' : '')} onClick={() => toggle(o)}>
          <span className="gtff-box" />{o}
        </button>
      ))}
      {q.other && (
        <div className={'gtff-opt gtff-opt--box gtff-opt--other' + (otherVal ? ' gtff-opt--on' : '')}>
          <button type="button" role="checkbox" aria-checked={!!otherVal} disabled={disabled} className="gtff-optbtn"
            onClick={() => setOther(!otherVal, otherText)}><span className="gtff-box" />Other</button>
          <OtherInput value={otherText} disabled={disabled} onChange={(t) => { setOtherText(t); setOther(true, t); }} />
        </div>
      )}
    </div>
  );
}

function Scale({ q, value, onChange, disabled }) {
  const nums = [];
  for (let i = q.min; i <= q.max; i++) nums.push(i);
  return (
    <div className="gtff-scale">
      <div className="gtff-scale-row" role="radiogroup">
        {nums.map((n) => (
          <button type="button" key={n} role="radio" aria-checked={Number(value) === n} disabled={disabled}
            className={'gtff-scale-n' + (Number(value) === n ? ' gtff-scale-n--on' : '')} onClick={() => onChange(n)}>{n}</button>
        ))}
      </div>
      {(q.min_label || q.max_label) && (
        <div className="gtff-scale-ends"><span>{q.min_label}</span><span>{q.max_label}</span></div>
      )}
    </div>
  );
}

function Control({ q, value, onChange, disabled }) {
  const text = (type, extra) => (
    <input className="gtff-input" type={type} value={value == null ? '' : value} disabled={disabled}
      onChange={(e) => onChange(e.target.value)} {...(extra || {})} />
  );
  switch (q.type) {
    case 'short': return text('text', { placeholder: 'Your answer', maxLength: 500 });
    case 'paragraph':
      return <textarea className="gtff-input gtff-area" rows={4} value={value || ''} disabled={disabled} maxLength={5000}
        placeholder="Your answer" onChange={(e) => onChange(e.target.value)} />;
    case 'choice': return <Choice q={q} value={value} onChange={onChange} disabled={disabled} />;
    case 'checkboxes': return <Checks q={q} value={value} onChange={onChange} disabled={disabled} />;
    case 'dropdown':
      return (
        <div className="gtff-selwrap">
          <select className="gtff-input gtff-select" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">Choose</option>
            {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    case 'scale': return <Scale q={q} value={value} onChange={onChange} disabled={disabled} />;
    case 'yesno':
    case 'put_forward':
      return (
        <div className="gtff-yn" role="radiogroup">
          {['yes', 'no'].map((v) => (
            <button type="button" key={v} role="radio" aria-checked={value === v} disabled={disabled}
              className={'gtff-yn-b' + (value === v ? ' gtff-yn-b--on' : '')} onClick={() => onChange(value === v && !q.required ? null : v)}>
              {v === 'yes' ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      );
    case 'date': return text('date');
    case 'daterange': {
      const v = value && typeof value === 'object' ? value : {};
      return (
        <div className="gtff-range">
          <label><span>From</span><input className="gtff-input" type="date" value={v.from || ''} disabled={disabled}
            onChange={(e) => onChange({ ...v, from: e.target.value })} /></label>
          <label><span>To</span><input className="gtff-input" type="date" value={v.to || ''} min={v.from || undefined} disabled={disabled}
            onChange={(e) => onChange({ ...v, to: e.target.value })} /></label>
        </div>
      );
    }
    case 'time': return text('time');
    case 'number': return text('text', { inputMode: 'decimal', placeholder: 'A number' });
    case 'email': return text('email', { placeholder: 'name@example.com', autoComplete: 'email' });
    case 'phone': return text('tel', { placeholder: '+44 7700 900000', autoComplete: 'tel' });
    case 'url': return text('url', { placeholder: 'https://' });
    default: return null;
  }
}

// The questions, each in its own card, as people see them.
export default function GtFormFields({ questions, answers, onChange, errors, disabled }) {
  const a = answers || {};
  const e = errors || {};
  return (
    <div className="gtff">
      {(questions || []).map((q) => (q.type === 'section' ? (
        <div key={q.key} className="gtff-section">
          <h3>{q.label}</h3>
          {q.help && <p>{q.help}</p>}
        </div>
      ) : (
        <div key={q.key} className={'gtff-q' + (e[q.key] ? ' gtff-q--err' : '')} id={'gtff-' + q.key}>
          <div className="gtff-label">{q.label}{q.required && <span className="gtff-req" aria-label="required">*</span>}</div>
          {q.help && <p className="gtff-help">{q.help}</p>}
          <Control q={q} value={a[q.key]} disabled={disabled} onChange={(v) => onChange && onChange(q.key, v)} />
          {e[q.key] && <div className="gtff-err">{e[q.key]}</div>}
        </div>
      )))}
    </div>
  );
}

// A filled form, read only: question, then answer.
export function GtAnswerList({ questions, answers, compact }) {
  const a = answers || {};
  const items = (questions || []).filter((q) => q.type !== 'section');
  return (
    <dl className={'gtff-answers' + (compact ? ' gtff-answers--compact' : '')}>
      {items.map((q) => {
        const t = answerText(q, a[q.key]);
        return (
          <div key={q.key} className="gtff-ans">
            <dt>{q.label}</dt>
            <dd className={t ? '' : 'gtff-ans-none'}>{t || 'No answer'}</dd>
          </div>
        );
      })}
    </dl>
  );
}
